/**
 * 13L Product Card — GSAP Scroll-Driven Sticky Benefits
 * Uses GSAP ScrollTrigger for smooth scroll-based animations.
 * On mobile (<=768px), falls back to IntersectionObserver.
 */

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.pc__item'));
    if (!this.items.length) return;

    this.track = this.querySelector('.pc__scroll-track');
    if (!this.track) return;

    this.isMobile = window.matchMedia('(max-width: 768px)');

    if (this.isMobile.matches) {
      this.initMobile();
    } else {
      this.initDesktop();
    }

    this.onResize = this.handleResize.bind(this);
    this.isMobile.addEventListener('change', this.onResize);
  }

  handleResize(e) {
    this.destroy();
    if (e.matches) {
      this.initMobile();
    } else {
      this.initDesktop();
    }
  }

  initDesktop() {
    this.mode = 'desktop';
    const itemCount = this.items.length;
    
    // Total track height giving enough scroll distance per item
    this.track.style.height = (itemCount * 80) + 'vh';

    // Clear initial CSS transforms
    this.items.forEach(item => {
      gsap.set(item, { opacity: 0, y: 30 });
      item.classList.remove('pc__item--active');
    });

    this.tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.track,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1 // Link directly to scroll with minimal smoothing
      }
    });

    this.items.forEach((item, i) => {
      const startTime = i * 2;
      
      this.tl.to(item, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'none',
        onStart: () => item.classList.add('pc__item--active'),
        onReverseComplete: () => item.classList.remove('pc__item--active')
      }, startTime);

      // If it's not the last item, dim it smoothly as the next one appears
      if (i < itemCount - 1) {
        this.tl.to(item, {
          opacity: 0.4,
          duration: 0.5,
          ease: 'none',
          onStart: () => item.classList.remove('pc__item--active'),
          onReverseComplete: () => item.classList.add('pc__item--active')
        }, startTime + 1.2);
      }
    });
  }

  initMobile() {
    this.mode = 'mobile';
    this.track.style.height = 'auto';

    /* Show all items, activate first */
    this.items.forEach((item, i) => {
      item.classList.toggle('pc__item--active', i === 0);
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.items.forEach((item) => {
              item.classList.toggle('pc__item--active', item === entry.target);
            });
          }
        });
      },
      {
        root: null,
        rootMargin: '-30% 0px -30% 0px',
        threshold: 0.3
      }
    );

    this.items.forEach((item) => this.observer.observe(item));
  }



  destroy() {
    if (this.tl) {
      if (this.tl.scrollTrigger) this.tl.scrollTrigger.kill();
      this.tl.kill();
      this.tl = null;
    }

    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
      this.scrollTrigger = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    /* Reset item states */
    this.items.forEach((item) => {
      item.classList.remove('pc__item--active');
      gsap.set(item, { clearProps: 'all' });
    });
  }

  disconnectedCallback() {
    this.destroy();
    if (this.isMobile) {
      this.isMobile.removeEventListener('change', this.onResize);
    }
  }
}

/* Registered as pdp-product-card: theme.js owns the product-card name for
   the PLP cards and registers it first, so defining under that name here
   was a silent no-op and this element never got its scroll behaviour. */
if (!customElements.get('pdp-product-card')) {
  customElements.define('pdp-product-card', ProductCard);
}
